import { Collapse, CollapseProps, Flex, Skeleton } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// components
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import ActionListHeaderComponent from 'modules/main/actions/listHeader';
import ListButton from 'components/listButton/listButton';
import NoData from 'components/noData/noData';
import Icon from 'utils/icon';

// services
import NotificationService from 'core/services/notification';

// models
import { dateFormat } from 'utils/dateFormat';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import InfiniteScroll from 'react-infinite-scroll-component';
import { onGetCommitteesList } from '../services/committeeServices';
import { isRTL } from 'core/services/language';
import { Committee } from '../models/committee';
import StatusComponent from 'components/status/status';
import { StatusColors } from 'utils/statusColors';
import { ServiceCost } from 'modules/main/actions/commonModels';
import ActiveCommitteeModal from '../components/activeCommitteeModal';
import CommitteeDocuments from '../components/committeeDocuments';

//styles

const panelStyle: React.CSSProperties = {
	padding: '10px 20px',
	marginBottom: 24,
	background: '#fff',
	borderRadius: 'var(--radius)',
	border: 'none',
};

const CommitteesDocumentsListPage: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [items, setItems] = useState<CollapseProps['items']>([]);

	const [selectModalOpen, setSelectModalOpen] = useState<boolean>(false);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	useEffect(() => {
		getCommittees();
	}, []);

	const getCommittees = async () => {
		try {
			setIsLoading(true);

			const response = await onGetCommitteesList();

			setItems(generateCommitteesItems(response.committees));
			setTotalRecords(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async () => {
		try {
			const response = await onGetCommitteesList(currentPage + 1);

			setItems((prevItems) => [...prevItems!, ...generateCommitteesItems(response.committees)!]);

			setCurrentPage(currentPage + 1);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const generateCommitteesItems = (committees: Committee[]): CollapseProps['items'] => {
		return committees.map((committee, index) => ({
			key: committee.id,
			style: panelStyle,
			children: <CommitteeDocuments committeeId={committee.id} refresh={getCommittees} />,
			label: (
				<Flex justify="space-between" align="center">
					<div>
						<Flex gap={100} align="center">
							<h6 className={`mb-0 fw-medium text-primary`}>
								{t('ACTIONS.COMMITTEE_NAME', { name: isRTL() ? committee.name.ar : committee.name.en })}
							</h6>
						</Flex>

						<p className="mt-2 mb-0 text-light">{dateFormat(committee.appointed_at)}</p>
					</div>

					{committee.status != 'draft' && (
						<StatusComponent
							style={{ padding: '2px 10px', borderRadius: 10 }}
							text={t(`COMMON.${committee.status.toUpperCase()}`)}
							color={StatusColors[committee.status]}
						/>
					)}
				</Flex>
			),
		}));
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.COMMITTEES_DOCUMENTS')}
				subTitle={t('ACTIONS.COMMITTEES_DESC')}
				newRequestText={t('ACTIONS.NEW_COMMITTEE_DOCUMENT')}
				customNavigate={() => setSelectModalOpen(true)}
			/>

			{isLoading ? (
				<LoadingComponent />
			) : items!.length === 0 ? (
				<NoData message={t('ACTIONS.NO_COMMITTEE_AVAILABLE')} />
			) : (
				<InfiniteScroll
					dataLength={items!.length}
					next={fetchMoreData}
					hasMore={totalRecords > items!.length}
					loader={<Skeleton.Button block active style={{ height: 84, borderRadius: 'var(--radius)' }} />}>
					{
						<Collapse
							items={items}
							bordered={false}
							style={{ background: '#f9fafb' }}
							expandIconPosition="end"
							expandIcon={(props) => (
								<Icon
									style={{ transform: props.isActive ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s' }}
									name="rightArrow"
									height={16}
									width={16}
									color="var(--primary)"
								/>
							)}
						/>
					}
				</InfiniteScroll>
			)}

			<ActiveCommitteeModal
				open={selectModalOpen}
				type="documents"
				onClose={() => setSelectModalOpen(false)}
				onSelect={(committeeId) => {
					setSelectModalOpen(false);
					navigate(`/dashboard/actions/boards/committees/${committeeId}/documents/create`);
				}}
			/>
		</>
	);
};

export default CommitteesDocumentsListPage;
